import { useCallback, useMemo } from 'react'

import { TextLink } from '@circlefin/components'
import { createColumnHelper } from '@circlefin/components/lib/AdvancedTable'
import { useDate, useMoney } from '@circlefin/hooks'
import { paymentDetail } from '@services/sections/lib/payments'
import { settlementAdvanceDetailSection } from '@services/sections/lib/settlementAdvance'
import { SettlementCreditRepaymentTransferType } from '@shared/graphql'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import type { SettlementCreditRepaymentTransfer } from '@shared/graphql'

const columnHelper = createColumnHelper<SettlementCreditRepaymentTransfer>()
const textClassNames = 'type-body-sm'

export const useSettlementCreditRepaymentTransfersColumns = (
  viewHistory = false,
) => {
  const { t } = useTranslation('lineOfCredit')
  const { t: tCommon } = useTranslation('common')
  const { date } = useDate()
  const { money } = useMoney()
  const router = useRouter()

  const onClick = useCallback(
    (id: string) => () => {
      void router.push({
        pathname: viewHistory
          ? settlementAdvanceDetailSection.route
          : paymentDetail.route,
        query: {
          id: id,
        },
      })
    },
    [router, viewHistory],
  )

  return useMemo(() => {
    /**
     * Date Column.
     */
    const createDate = columnHelper.accessor('createDate', {
      header: t<string>('repaymentsTable.headers.date'),
      size: 95,
      cell: ({ getValue }) => (
        <span className={textClassNames}>{date(getValue(), 'dateTime')}</span>
      ),
    })

    /**
     * Amount column.
     */
    const amount = columnHelper.accessor('amount', {
      header: t<string>('repaymentsTable.headers.amount'),
      size: 95,
      cell: ({ getValue }) => {
        const value = getValue()

        return (
          <span className={textClassNames}>
            {money({
              number: value.amount,
              variant: value.currency,
              options: {
                symbol: false,
                signDisplay: false,
              },
            })}
          </span>
        )
      },
    })

    const type = columnHelper.accessor('type', {
      header: t<string>('repaymentsTable.headers.type'),
      size: 240,
      cell: ({ getValue }) => (
        <span className={textClassNames}>
          {t(`repaymentsTable.typeLabels.${getValue()}`)}
        </span>
      ),
    })

    /**
     * View Column.
     */
    const view = columnHelper.display({
      id: 'action',
      size: 100,
      meta: {
        className: 'text-center',
      },
      cell: ({ row: { original } }) => {
        if (original.type === SettlementCreditRepaymentTransferType.fiat) {
          return null
        }

        return (
          <TextLink
            onClick={onClick(
              viewHistory ? original.creditTransferId : original.paymentId,
            )}
            variant="primary"
          >
            {tCommon('view')}
          </TextLink>
        )
      },
    })

    return [createDate, amount, type, view]
  }, [t, tCommon, viewHistory, date, money, onClick])
}
